"use client";

import { useState, useEffect, useRef } from "react";
import { storyblokEditable, StoryblokComponent } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";
import { cn } from "@/lib/utils";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

type ShapeGalleryStoryblok = {
  headline?: string;
  items?: any[];
  _uid: string;
  component: "shape_gallery";
  [k: string]: any;
};

type ShapeGalleryProps = {
  blok: ShapeGalleryStoryblok;
};

const ShapeGallery = ({ blok }: ShapeGalleryProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  );

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;
    try {
      if (isInView && !lightboxOpen) {
        autoplay.play();
      } else {
        autoplay.stop();
      }
    } catch {
      // Carousel may not be active (e.g. hidden on desktop)
    }
  }, [api, isInView, lightboxOpen]);

  const itemCount = blok.items?.length || 0;
  const isFourItems = itemCount === 4;
  const remainder = !isFourItems ? itemCount % 3 : 0;
  const lastRowStart = itemCount - remainder;

  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full ">
      <section className="py-8 md:py-12 flex flex-col gap-4 w-full items-center">
        {blok.headline && (
          <h2 className="text-headline-xl text-black text-center px-4 mb-2">
            {blok.headline}
          </h2>
        )}

        {/* Mobile: scrollable carousel */}
        <div className="md:hidden w-full">
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: false,
              dragFree: false,
              watchResize: true,
            }}
            plugins={[autoplayRef.current]}
            className="w-full"
          >
            <CarouselContent className="ml-0 pl-4 items-stretch">
              {blok.items?.map((nestedBlok: any) => (
                <CarouselItem
                  key={nestedBlok._uid}
                  className="pl-4 basis-auto h-auto"
                >
                  <StoryblokComponent blok={nestedBlok} />
                </CarouselItem>
              ))}
              <div className="shrink-0 w-4" />
            </CarouselContent>
          </Carousel>
        </div>

        {/* Tablet & Desktop: static layout */}
        <div className="hidden md:grid grid-cols-12 gap-4 lg:gap-6 px-8 lg:px-0 mx-auto max-w-7xl">
          {blok.items?.map((nestedBlok: any, index: number) => (
            <div
              key={nestedBlok._uid}
              className={cn(
                "flex justify-center",
                isFourItems ? "col-span-6 lg:col-span-5" : "col-span-4",
                isFourItems && index % 2 === 0 && "lg:col-start-2",
                !isFourItems &&
                  remainder === 2 &&
                  index === lastRowStart &&
                  "col-start-3",
                !isFourItems &&
                  remainder === 1 &&
                  index === lastRowStart &&
                  "col-start-5",
              )}
            >
              <StoryblokComponent blok={nestedBlok} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ShapeGallery;
